import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  CalendarClock,
  Check,
  ChevronDown,
  Clock3,
  Eye,
  Headphones,
  MapPin,
  Music2,
  Pencil,
  Search,
  SlidersHorizontal,
  Star,
  Store,
  Upload,
  Youtube,
} from "lucide-react";
import { api, fmtPrice } from "../lib/api";
import { REGULAR_ENGINEER_PRICE_CAP } from "../lib/engineerPricing";
import ServiceBookingDrawer from "../components/ServiceBookingDrawer";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const demoEngineers = [
  {
    id: "arya-pratama",
    name: "Arya Pratama",
    marketplace_tier: "verified",
    initials: "AP",
    role: "Mixing & Mastering Engineer",
    location: "Jakarta",
    specialties: ["Mixing", "Mastering"],
    genres: ["Pop", "R&B", "Alternative"],
    rating: 4.9,
    reviews: 47,
    years_experience: 8,
    projects: 186,
    price: 850000,
    response: "± 1 jam",
    turnaround: "3–5 days",
    accent: "from-rose-500 to-orange-400",
    avatar_url: "/images/engineers/demo-studio-engineer.webp",
    hero_image_url: "/images/engineers/demo-studio-engineer.webp",
    bio: "Membantu musisi mendapatkan mix yang lebar, vokal yang dekat, dan master yang siap rilis di semua platform.",
  },
  {
    id: "naya-sound",
    name: "Naya Anindita",
    marketplace_tier: "regular",
    initials: "NA",
    role: "Vocal Producer & Mixing Engineer",
    location: "Bandung",
    specialties: ["Mixing"],
    genres: ["Indie Pop", "Soul", "Jazz"],
    rating: 5.0,
    reviews: 32,
    years_experience: 6,
    projects: 114,
    price: 700000,
    response: "± 2 jam",
    turnaround: "4–6 days",
    accent: "from-violet-500 to-fuchsia-400",
    avatar_url: "/images/engineers/demo-vocal-producer.webp",
    hero_image_url: "/images/engineers/demo-vocal-producer.webp",
    bio: "Focused on vocal character, ambient detail, and musical dynamics that still feel natural.",
  },
  {
    id: "rama-audio",
    name: "Rama Wicaksana",
    marketplace_tier: "regular",
    initials: "RW",
    role: "Recording & Mastering Engineer",
    location: "Yogyakarta",
    specialties: ["Recording", "Mastering"],
    genres: ["Rock", "Metal", "Electronic"],
    rating: 4.8,
    reviews: 61,
    years_experience: 10,
    projects: 243,
    price: 500000,
    response: "± 3 jam",
    turnaround: "2–4 days",
    accent: "from-sky-500 to-cyan-300",
    avatar_url: "/images/engineers/demo-mastering-engineer.webp",
    hero_image_url: "/images/engineers/demo-mastering-engineer.webp",
    bio: "Clean recording and powerful mastering for modern releases, from live bands to electronic productions.",
  },
];

const services = [
  { name: "Stereo Mastering", description: "Final master ready for digital distribution", price: 500000, max_audio_tracks: 1, days: "2 days", revisions: 1 },
  { name: "Full Mixing", description: "Up to 40 stems with light vocal tuning", price: 850000, max_audio_tracks: 40, days: "4 days", revisions: 2, popular: true },
  { name: "Mix + Master", description: "Complete mix and release-ready master", price: 1250000, max_audio_tracks: 40, days: "5 days", revisions: 3 },
];

function EngineerAvatar({ engineer, large = false }) {
  return (
    <div className={`${large ? "w-24 h-24 md:w-28 md:h-28 text-2xl rounded-3xl border-4 border-[#0a0a0c]" : "w-11 h-11 sm:w-14 sm:h-14 text-xs sm:text-sm rounded-xl sm:rounded-2xl border border-white/10"} bg-gradient-to-br ${engineer.accent} flex items-center justify-center font-[Outfit] font-black text-white shadow-xl flex-shrink-0 overflow-hidden`}>
      {engineer.avatar_url ? <img src={engineer.avatar_url} alt={`${engineer.name} profile`} className="w-full h-full object-cover" loading="lazy" /> : engineer.initials}
    </div>
  );
}

function VerifiedBadge({ large = false }) {
  return <span role="img" aria-label="Verified Engineer" title="Verified Engineer · verified by TripleSide Studio" className="inline-flex flex-shrink-0 drop-shadow-[0_1px_5px_rgba(14,165,233,0.55)]"><BadgeCheck aria-hidden="true" className={large ? "w-7 h-7" : "w-4 h-4 sm:w-5 sm:h-5"} fill="#0ea5e9" stroke="white" strokeWidth={2.4} /></span>;
}

function Rating({ engineer }) {
  if (!Number(engineer.reviews)) return <div className="text-xs text-zinc-500">No ratings yet</div>;
  return (
    <div className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm">
      <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" fill="currentColor" />
      <span className="font-bold">{Number(engineer.rating || 0).toFixed(1)}</span>
      <span className="text-zinc-500">({engineer.reviews})</span>
    </div>
  );
}

const youtubeEmbed = (value) => {
  try {
    const url = new URL(value);
    const parts = url.pathname.split("/").filter(Boolean);
    const id = url.hostname.includes("youtu.be")
      ? parts[0]
      : url.pathname === "/watch"
        ? url.searchParams.get("v")
        : parts[1];
    return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0` : "";
  } catch {
    return "";
  }
};

const spotifyEmbed = (value) => {
  try {
    const url = new URL(value);
    if (url.hostname !== "open.spotify.com") return "";
    const path = url.pathname.replace(/^\/embed/, "");
    return `https://open.spotify.com/embed${path}`;
  } catch {
    return "";
  }
};

function PortfolioPlayer({ type, item, preview }) {
  if (!item && !preview) {
    return <div className="aspect-video rounded-2xl border border-dashed border-white/10 bg-black/40 flex items-center justify-center text-sm text-zinc-500">Portfolio not available yet.</div>;
  }
  if (type === "youtube") {
    return (
      <div className="aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black">
        <iframe
          className="w-full h-full"
          src={youtubeEmbed(item?.source_url) || "https://www.youtube-nocookie.com/embed/dQw4w9WgXcQ?rel=0"}
          title="Contoh portofolio YouTube"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (type === "spotify") {
    return (
      <iframe
        className="w-full rounded-2xl border-0"
        style={{ minHeight: 152 }}
        src={spotifyEmbed(item?.source_url) || "https://open.spotify.com/embed/track/0VjIjW4GlUZAMYd2vXMi3b"}
        title="Contoh portofolio Spotify"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
      />
    );
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#151519] to-[#09090b] p-5">
      <div className="flex items-center gap-4 mb-5">
        <div className="w-12 h-12 rounded-xl bg-[#e11d48]/15 flex items-center justify-center">
          <Music2 className="w-5 h-5 text-[#e11d48]" />
        </div>
        <div>
          <div className="font-semibold">{item?.title || "Midnight Drive — Final Master"}</div>
          <div className="text-xs text-zinc-500 mt-1">Audio upload · MP3 preview</div>
        </div>
      </div>
      <audio controls preload="none" className="w-full h-10" aria-label="Contoh audio upload">
        <source src={item?.source_url || "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3"} />
      </audio>
    </div>
  );
}

export default function EngineerMarketplacePreview({ preview = false }) {
  const [serviceFilter, setServiceFilter] = useState("All services");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalEngineers, setTotalEngineers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState(null);
  const [portfolioType, setPortfolioType] = useState("youtube");
  const [portfolioIndex, setPortfolioIndex] = useState(0);
  const [catalogEngineers, setCatalogEngineers] = useState(preview ? demoEngineers : []);
  const [usingDemoData, setUsingDemoData] = useState(!!preview);
  const [bookingService, setBookingService] = useState(null);
  const [shopLoading, setShopLoading] = useState(false);
  const [shopLoadError, setShopLoadError] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (preview && usingDemoData && (debouncedQuery || serviceFilter !== "All services")) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setLoadError(false);
    api.get("/engineers", { params: {
      page,
      ...(debouncedQuery ? { search: debouncedQuery } : {}),
      ...(serviceFilter !== "All services" ? { service: serviceFilter } : {}),
    } })
      .then(({ data }) => {
        if (!active) return;
        if (!Array.isArray(data?.items)) throw new Error("Invalid engineer list response");
        if (preview && data.total === 0 && page === 1 && !debouncedQuery && serviceFilter === "All services") {
          setCatalogEngineers(demoEngineers);
          setUsingDemoData(true);
          setLoading(false);
          return;
        }
        if (page > 1 && page > Math.max(1, data.total_pages)) {
          setPage(Math.max(1, data.total_pages));
          return;
        }
        const accents = ["from-rose-500 to-orange-400", "from-violet-500 to-fuchsia-400", "from-sky-500 to-cyan-300"];
        setCatalogEngineers(data.items.map((engineer, index) => {
          const activeServices = (engineer.services || []).filter((service) => service.active !== false);
          const prices = activeServices.map((service) => Number(service.price || 0)).filter((price) => price > 0);
          const turnaroundDays = activeServices.map((service) => Number(service.turnaround_days || 0)).filter(Boolean);
          return {
            ...engineer,
            initials: engineer.name.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase(),
            role: engineer.professional_title,
            reviews: engineer.review_count || 0,
            projects: engineer.completed_projects || 0,
            price: prices.length ? Math.min(...prices) : 0,
            response: engineer.response_time ? `Estimated response: ${engineer.response_time}` : "Response time unavailable",
            turnaround: turnaroundDays.length ? `${Math.min(...turnaroundDays)}–${Math.max(...turnaroundDays)} days` : "Based on brief",
            accent: accents[index % accents.length],
          };
        }));
        setTotalEngineers(data.total);
        setTotalPages(data.total_pages);
        setUsingDemoData(false);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        if (!preview) setLoadError(true);
        setLoading(false);
      });
    return () => { active = false; };
  }, [preview, page, serviceFilter, debouncedQuery, reloadKey, usingDemoData]);

  const visibleEngineers = useMemo(() => {
    if (!usingDemoData) return catalogEngineers;
    const normalized = query.trim().toLowerCase();
    return catalogEngineers.filter((engineer) => {
      const matchesService =
        serviceFilter === "All services" || engineer.specialties.includes(serviceFilter);
      const matchesQuery =
        !normalized ||
        [engineer.name, engineer.role, engineer.location, ...engineer.genres]
          .join(" ")
          .toLowerCase()
          .includes(normalized);
      return matchesService && matchesQuery;
    }).sort((a, b) => {
      const tierDifference = Number(b.marketplace_tier === "verified") - Number(a.marketplace_tier === "verified");
      return tierDifference || Number(b.rating || 0) - Number(a.rating || 0) || Number(b.projects || 0) - Number(a.projects || 0) || Number(b.reviews || 0) - Number(a.reviews || 0);
    });
  }, [catalogEngineers, query, serviceFilter, usingDemoData]);
  const resultCount = usingDemoData ? visibleEngineers.length : totalEngineers;
  const pageCount = usingDemoData ? Math.ceil(resultCount / 12) : totalPages;
  const pageNumbers = [...new Set([1, page - 1, page, page + 1, pageCount])]
    .filter((number) => number >= 1 && number <= pageCount)
    .sort((a, b) => a - b);
  const goToPage = (nextPage) => {
    if (nextPage === page || nextPage < 1 || nextPage > pageCount) return;
    setPage(nextPage);
    document.getElementById("engineer-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const selectEngineer = (engineer) => {
    setSelected(engineer);
    setPortfolioType(engineer.portfolio?.[0]?.source_type || "youtube");
    setPortfolioIndex(0);
    setShopLoadError(false);
    if (usingDemoData || !engineer.slug) {
      setShopLoading(false);
      return;
    }
    setShopLoading(true);
    api.get(`/engineers/${encodeURIComponent(engineer.slug)}`)
      .then(({ data }) => {
        setSelected((current) => current?.id === engineer.id ? {
          ...current,
          ...data,
          role: data.professional_title || current.role,
          reviews: data.review_count || 0,
          projects: data.completed_projects || 0,
        } : current);
      })
      .catch(() => setShopLoadError(true))
      .finally(() => setShopLoading(false));
  };

  if (selected) {
    const displayedServices = (selected.services?.length ? selected.services.filter((service) => service.active !== false) : preview ? services : []).filter((service) => selected.marketplace_tier === "verified" || Number(service.price) <= REGULAR_ENGINEER_PRICE_CAP);
    const portfolioTypes = selected.portfolio?.length
      ? [...new Set(selected.portfolio.map((item) => item.source_type))]
      : preview ? ["youtube", "spotify", "audio"] : [];
    const portfolioItems = (selected.portfolio || []).filter((item) => item.source_type === portfolioType);
    const activePortfolio = portfolioItems[portfolioIndex] || portfolioItems[0];
    return (
      <div data-testid="engineer-profile-preview" className="min-h-screen pt-24 pb-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <button onClick={() => setSelected(null)} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to All Engineers
            </button>
            {preview && <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-400/20 bg-amber-400/10 text-amber-300 text-[10px] font-bold uppercase tracking-[0.18em]">
              <Eye className="w-3.5 h-3.5" /> Admin preview
            </div>}
          </div>

          <section className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0a0a0c] min-h-[470px] md:min-h-[430px]">
            {selected.hero_image_url ? <img src={selected.hero_image_url} alt={`${selected.name} di studio`} className="absolute inset-0 w-full h-full object-cover" /> : <div className={`absolute inset-0 bg-gradient-to-br ${selected.accent} opacity-25`} />}
            <div className="absolute inset-0 bg-gradient-to-t from-[#060607] via-black/35 to-black/10" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/35 via-transparent to-transparent" />
            <div className="relative min-h-[470px] md:min-h-[430px] p-7 md:p-10 flex flex-col justify-end">
              <div className="flex flex-col md:flex-row md:items-end gap-5 md:gap-6">
                <EngineerAvatar engineer={selected} large />
                <div className="flex-1 min-w-0 drop-shadow-lg">
                  <div className="flex items-center gap-2">
                    <h1 className="font-[Outfit] text-3xl md:text-5xl font-black">{selected.name}</h1>
                    {selected.marketplace_tier === "verified" && <VerifiedBadge large />}
                  </div>
                  <p className="text-zinc-200 mt-1 font-medium">{selected.role}</p>
                  <span className={`inline-flex mt-2 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${selected.marketplace_tier === "verified" ? "border-sky-400/30 bg-sky-400/10 text-sky-200" : "border-white/20 bg-black/30 text-zinc-300"}`}>{selected.marketplace_tier === "verified" ? "Verified Engineer" : "Regular Engineer"}</span>
                  <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm text-zinc-300">
                    <Rating engineer={selected} />
                    <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {selected.location}</span>
                    {Number(selected.years_experience) > 0 && (
                      <span className="flex items-center gap-1.5">
                        <BriefcaseBusiness className="w-4 h-4" />
                        {selected.years_experience} {Number(selected.years_experience) === 1 ? "Year" : "Years"} Experience
                      </span>
                    )}
                  </div>
                </div>
                <button disabled={!displayedServices.length} onClick={() => displayedServices[0] && setBookingService(displayedServices[0])} className="px-6 py-3.5 rounded-full bg-[#e11d48] hover:bg-[#be123c] disabled:opacity-40 font-semibold flex items-center justify-center gap-2 transition-colors shadow-xl shadow-black/30">
                  Choose a Service <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </section>

          <div className="grid lg:grid-cols-[1fr_340px] gap-7 mt-7">
            <div className="space-y-7">
              <section className="rounded-3xl border border-white/10 bg-[#0a0a0c] p-7">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#e11d48] mb-3">Tentang engineer</div>
                <p className="text-zinc-300 leading-relaxed">{selected.bio}</p>
                <div className="flex flex-wrap gap-2 mt-5">
                  {selected.genres.map((genre) => <span key={genre} className="px-3 py-1.5 rounded-full bg-white/5 text-xs text-zinc-300">{genre}</span>)}
                </div>
              </section>

              <section className="rounded-3xl border border-white/10 bg-[#0a0a0c] p-7">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#e11d48] mb-2">Selected works</div>
                    <h2 className="font-[Outfit] text-2xl font-bold">Portofolio</h2>
                  </div>
                  <div className="flex gap-1 p-1 rounded-full bg-black/50 border border-white/10">
                    {portfolioTypes.map((value) => {
                      const config = value === "youtube" ? ["YouTube", Youtube] : value === "spotify" ? ["Spotify", Headphones] : ["Upload", Upload];
                      const [label, Icon] = config;
                      return (
                      <button
                        key={value}
                        onClick={() => { setPortfolioType(value); setPortfolioIndex(0); }}
                        className={`px-3 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${portfolioType === value ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}
                      >
                        <Icon className="w-3.5 h-3.5" /> {label}
                      </button>
                    );})}
                  </div>
                </div>
                <PortfolioPlayer type={portfolioType} item={activePortfolio} preview={preview} />
                <div className="mt-4">
                  <div className="font-semibold">{activePortfolio?.title || (preview ? "Neon Afterglow" : "No work available yet")}</div>
                  <div className="text-xs text-zinc-500 mt-1">{activePortfolio ? [activePortfolio.contribution, activePortfolio.genre, activePortfolio.release_year].filter(Boolean).join(" · ") : preview ? "Mixing & mastering · Pop / R&B · 2026" : ""}</div>
                </div>
                {portfolioItems.length > 1 && <div className="mt-5 flex flex-wrap gap-2" aria-label={`Choose ${portfolioType} work`}>{portfolioItems.map((item, index) => <button key={item.id || `${item.source_url}-${index}`} type="button" onClick={() => setPortfolioIndex(index)} className={`rounded-full border px-3 py-2 text-xs transition-colors ${activePortfolio === item ? "border-[#e11d48] bg-[#e11d48]/15 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}>{item.title}</button>)}</div>}
              </section>
            </div>

            <aside className="space-y-4">
              <div className="flex items-center justify-between px-1">
                <h2 className="font-[Outfit] text-xl font-bold">Service Packages</h2>
                <span className="text-xs text-zinc-500">{displayedServices.length} available</span>
              </div>
              {displayedServices.map((service) => (
                <button key={service.id || service.name} onClick={() => setBookingService(service)} className={`relative w-full text-left rounded-2xl border p-5 transition-all hover:-translate-y-0.5 ${service.popular ? "border-[#e11d48]/60 bg-[#e11d48]/5" : "border-white/10 bg-[#0a0a0c] hover:border-white/20"}`}>
                  {service.popular && <span className="absolute -top-2.5 right-4 px-2.5 py-1 rounded-full bg-[#e11d48] text-[9px] uppercase tracking-wider font-bold">Populer</span>}
                  <div className="font-bold">{service.name}</div>
                  <div className="text-xs text-zinc-500 mt-1.5 leading-relaxed">{service.description}</div>
                  <div className="flex items-end justify-between mt-5">
                    <div>
                      <div className="text-[10px] text-zinc-500">Starting at</div>
                      <div className="font-[Outfit] text-xl font-bold">{fmtPrice(service.price)}</div>
                    </div>
                    <div className="text-right text-[10px] text-zinc-500">
                      <div>{service.days || `${service.turnaround_days} days`}</div>
                      <div>{service.revisions}× revisi</div>
                      {service.max_audio_tracks && <div>Maks. {service.max_audio_tracks} track</div>}
                    </div>
                  </div>
                </button>
              ))}
            </aside>
          </div>

          <section className="mt-7 rounded-3xl border border-white/10 bg-[#0a0a0c] p-7">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#e11d48] mb-2"><Store className="w-3.5 h-3.5" /> Digital shop</div>
                <h2 className="font-[Outfit] text-2xl font-bold">Produk dari {selected.name}</h2>
                <p className="text-xs text-zinc-500 mt-1">Sample packs, presets, templates, e-books, and other digital products.</p>
              </div>
              {!shopLoading && <span className="text-xs text-zinc-500">{selected.shop_product_count ?? selected.shop_products?.length ?? 0} products</span>}
            </div>

            {shopLoading ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" aria-label="Loading engineer products">{[0, 1, 2].map((item) => <div key={item} className="h-72 animate-pulse rounded-2xl bg-white/5" />)}</div> : shopLoadError ? <div className="rounded-2xl border border-amber-400/20 bg-amber-400/5 p-6 text-center text-sm text-amber-200">Products could not be loaded. Please reopen this profile.</div> : selected.shop_products?.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{selected.shop_products.map((product) => <Link key={product.id} to={`/shop/${product.id}`} className="group overflow-hidden rounded-2xl border border-white/10 bg-black/30 transition-all hover:-translate-y-1 hover:border-[#e11d48]/40"><div className="aspect-square overflow-hidden bg-black"><img src={product.image_url} alt={product.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div><div className="p-4"><div className="text-[9px] font-bold uppercase tracking-wider text-[#fb7185]">{product.category}</div><h3 className="mt-1.5 line-clamp-2 font-[Outfit] text-lg font-bold">{product.name}</h3><div className="mt-3 flex items-center justify-between gap-3"><span className={`font-[Outfit] text-lg font-black ${product.is_free ? "text-emerald-400" : ""}`}>{product.is_free ? "Free" : fmtPrice(product.price)}</span><ArrowRight className="w-4 h-4 text-zinc-600 transition-transform group-hover:translate-x-1 group-hover:text-[#fb7185]" /></div></div></Link>)}</div> : <div className="rounded-2xl border border-dashed border-white/10 bg-black/20 px-6 py-12 text-center"><Store className="w-8 h-8 mx-auto text-zinc-700" /><div className="mt-3 font-semibold text-zinc-300">No published products yet</div><p className="mt-1 text-xs text-zinc-600">This engineer&apos;s digital products will appear here.</p></div>}
          </section>
          {bookingService && <ServiceBookingDrawer engineer={selected} service={bookingService} preview={preview} onClose={() => setBookingService(null)} />}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="engineer-marketplace-preview" className="min-h-screen pt-24 sm:pt-28 md:pt-32 pb-24 md:pb-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          {preview && <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-400/20 bg-amber-400/10 text-amber-300 text-[10px] font-bold uppercase tracking-[0.18em]">
            <Eye className="w-3.5 h-3.5" /> Admin preview · belum live
          </div>}
          <div className="flex items-center gap-3">
            {preview && <span className="hidden sm:inline text-xs text-zinc-600">{usingDemoData ? "Data engineer pada halaman ini hanya contoh" : "Menampilkan data terverifikasi dari database"}</span>}
            {preview && <Link to="/admin/engineers" className="px-4 py-2 rounded-full border border-white/10 hover:bg-white/5 text-xs font-semibold flex items-center gap-2">
              <Pencil className="w-3.5 h-3.5" /> Kelola engineer
            </Link>}
          </div>
        </div>

        <section className="mb-6 md:mb-8 fade-up">
          <div className="hidden sm:block text-[10px] font-bold uppercase tracking-[0.3em] text-[#e11d48] mb-3">Marketplace · 04</div>
          <div className="max-w-4xl">
            <h1 className="pb-1 font-[Outfit] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05]">Engineer Marketplace</h1>
            <p className="mt-3 sm:mt-4 max-w-xl text-sm sm:text-base leading-relaxed text-zinc-400">Find the right engineer for your best sound. Listen to portfolios, compare services, and book sessions with engineers approved by <span translate="no" className="notranslate">TripleSide Studio</span>.</p>

            {!preview && <div className="mt-5 flex flex-wrap items-center gap-2.5">
              <span className="mr-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">For engineers</span>
              <Link to="/engineer/register" className="inline-flex items-center justify-center rounded-full bg-[#e11d48] px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-[#be123c]">Register as Engineer</Link>
              <Link to="/engineer/login" className="inline-flex items-center justify-center rounded-full border border-white/15 px-4 py-2.5 text-xs font-semibold text-zinc-200 transition-colors hover:bg-white/5 hover:text-white">Login</Link>
              <Dialog>
                <DialogTrigger asChild>
                  <button type="button" className="px-2 py-2 text-xs text-zinc-500 underline underline-offset-4 transition-colors hover:text-zinc-300">Terms</button>
                </DialogTrigger>
                <DialogContent className="w-[calc(100%-2rem)] max-w-lg rounded-2xl border-emerald-400/30 bg-[#0a0a0c] p-6 text-white sm:p-8">
                  <DialogTitle className="pr-8 font-[Outfit] text-xl leading-snug">Engineer Registration Terms</DialogTitle>
                  <div className="inline-flex w-fit items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-2 text-xs font-semibold text-emerald-200"><Clock3 className="h-3.5 w-3.5" /> Estimated 3–7 business days</div>
                  <DialogDescription className="text-sm leading-6 text-zinc-300">Every engineer registration and verification request is reviewed according to quality standards and the needs of <span translate="no" className="notranslate">TripleSide Studio</span>. The decision to accept, reject, or approve an engineer profile remains entirely at <span translate="no" className="notranslate">TripleSide Studio&apos;s</span> discretion.</DialogDescription>
                  <p className="text-xs leading-5 text-zinc-400">The estimated review and approval time begins once the submission is complete.</p>
                  <DialogClose asChild><button type="button" className="mt-2 w-fit rounded-full border border-white/20 px-4 py-2 text-xs font-semibold text-white hover:bg-white/10">Close</button></DialogClose>
                </DialogContent>
              </Dialog>
            </div>}
          </div>
        </section>

        <section className="grid grid-cols-[minmax(0,1fr)_8.5rem] gap-2 lg:grid-cols-[minmax(0,1fr)_13rem] lg:gap-3">
          <div className="relative min-w-0">
            <Search className="absolute left-3 lg:left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 lg:w-4 lg:h-4 text-zinc-500" />
            <input
              value={query}
              onChange={(event) => { setQuery(event.target.value); setPage(1); }}
              maxLength={100}
              placeholder="Search engineers..."
              className="w-full h-10 lg:h-12 pl-9 lg:pl-11 pr-3 lg:pr-4 rounded-lg lg:rounded-xl bg-[#0a0a0c] border border-white/10 text-xs lg:text-sm focus:outline-none focus:border-[#e11d48]/70"
            />
          </div>
          <div className="relative min-w-0">
            <SlidersHorizontal className="absolute left-3 lg:left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 lg:w-4 lg:h-4 text-zinc-500 pointer-events-none" />
            <select
              value={serviceFilter}
              onChange={(event) => { setServiceFilter(event.target.value); setPage(1); }}
              aria-label="Filter by service"
              className="w-full h-10 lg:h-12 pl-8 lg:pl-11 pr-7 lg:pr-10 appearance-none rounded-lg lg:rounded-xl bg-[#0a0a0c] border border-white/10 text-[11px] sm:text-xs lg:text-sm focus:outline-none focus:border-[#e11d48]/70"
            >
              <option>All services</option>
              <option>Mixing</option>
              <option>Mastering</option>
              <option>Recording</option>
            </select>
            <ChevronDown className="absolute right-2.5 lg:right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 lg:w-4 lg:h-4 text-zinc-500 pointer-events-none" />
          </div>
        </section>

        {loadError && <div role="alert" className="mt-4 lg:mt-10 mb-4 rounded-2xl border border-white/10 bg-[#0a0a0c] p-6 text-sm text-zinc-300">
          The engineer list could not be loaded. <button type="button" onClick={() => setReloadKey((value) => value + 1)} className="ml-2 font-semibold text-white underline underline-offset-4">Try again</button>
        </div>}

        <section id="engineer-results" className="mt-4 lg:mt-10 scroll-mt-28 grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 lg:gap-5">
          {!loading && !loadError && (usingDemoData ? visibleEngineers.slice((page - 1) * 12, page * 12) : visibleEngineers).map((engineer) => (
            <article key={engineer.id} role="button" tabIndex={0} onClick={() => selectEngineer(engineer)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); selectEngineer(engineer); } }} aria-label={`View ${engineer.name}'s profile`} className="group w-full h-full text-left rounded-xl sm:rounded-2xl border border-white/10 bg-[#0a0a0c] p-3 sm:p-4 lg:p-6 cursor-pointer hover:border-[#e11d48]/40 sm:hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e11d48] transition-all duration-300">
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:gap-4">
                <EngineerAvatar engineer={engineer} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <h3 className="font-[Outfit] text-sm sm:text-base lg:text-lg font-bold truncate">{engineer.name}</h3>
                    {engineer.marketplace_tier === "verified" && <VerifiedBadge />}
                  </div>
                  <p className="mt-0.5 truncate text-[10px] text-zinc-400 sm:text-xs">{engineer.role}</p>
                  <p className={`mt-1 truncate text-[9px] sm:text-[10px] font-semibold ${engineer.marketplace_tier === "verified" ? "text-sky-300" : "text-zinc-500"}`}>{engineer.marketplace_tier === "verified" ? "Verified Engineer" : "Regular Engineer"}</p>
                  <div className="mt-1.5 sm:mt-2"><Rating engineer={engineer} /></div>
                </div>
              </div>

              <div className="hidden sm:flex flex-wrap gap-2 mt-4 lg:mt-5">
                {engineer.genres.map((genre) => <span key={genre} className="px-2.5 py-1 rounded-full bg-white/5 text-[10px] text-zinc-400">{genre}</span>)}
              </div>

              <div className="hidden sm:grid grid-cols-2 gap-2 lg:gap-3 py-4 lg:py-5 mt-4 lg:mt-5 border-y border-white/5 text-[10px] lg:text-xs">
                <div className="flex min-w-0 items-center gap-1.5 lg:gap-2 text-zinc-400"><MapPin className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{engineer.location}</span></div>
                <div className="flex min-w-0 items-center gap-1.5 lg:gap-2 text-zinc-400"><Clock3 className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{engineer.turnaround}</span></div>
                {Number(engineer.years_experience) > 0 && (
                  <div className="flex min-w-0 items-center gap-1.5 lg:gap-2 text-zinc-400">
                    <BriefcaseBusiness className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{engineer.years_experience} {Number(engineer.years_experience) === 1 ? "Year" : "Years"} Experience</span>
                  </div>
                )}
                <div className="flex min-w-0 items-center gap-1.5 lg:gap-2 text-zinc-400"><CalendarClock className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{engineer.response}</span></div>
              </div>

              <div className="flex items-end justify-between gap-2 mt-4 lg:mt-5">
                <div className="min-w-0">
                  <div className="text-[9px] sm:text-[10px] text-zinc-500">Starting at</div>
                  <div className="truncate font-[Outfit] text-sm sm:text-base lg:text-xl font-bold">{fmtPrice(engineer.price)}</div>
                </div>
                <span aria-hidden="true" className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 shrink-0 rounded-full bg-white text-black flex items-center justify-center group-hover:bg-[#e11d48] group-hover:text-white transition-colors">
                  <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </span>
              </div>
            </article>
          ))}
        </section>

        {!loading && !loadError && resultCount === 0 && (
          <div className="mt-5 rounded-2xl border border-dashed border-white/10 py-16 text-center">
            <Search className="w-7 h-7 mx-auto text-zinc-600 mb-3" />
            <p className="text-sm text-zinc-400">{query.trim() || serviceFilter !== "All services" ? "No engineers match this search." : "No engineers have been published yet."}</p>
          </div>
        )}

        {!loading && !loadError && pageCount > 1 && <nav aria-label="Halaman daftar engineer" className="mt-8 flex flex-wrap items-center justify-center gap-2">
          <button type="button" onClick={() => goToPage(page - 1)} disabled={page === 1} className="rounded-full border border-white/15 px-4 py-2 text-sm text-zinc-300 hover:border-white/40 disabled:cursor-not-allowed disabled:opacity-40">Previous</button>
          {pageNumbers.map((number, index) => <React.Fragment key={number}>
            {index > 0 && number - pageNumbers[index - 1] > 1 && <span className="px-1 text-zinc-500" aria-hidden="true">…</span>}
            <button type="button" onClick={() => goToPage(number)} aria-label={`Halaman ${number}`} aria-current={number === page ? "page" : undefined} className={`min-w-10 rounded-full border px-3 py-2 text-sm font-semibold ${number === page ? "border-[#e11d48] bg-[#e11d48] text-white" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>{number}</button>
          </React.Fragment>)}
          <button type="button" onClick={() => goToPage(page + 1)} disabled={page === pageCount} className="rounded-full border border-white/15 px-4 py-2 text-sm text-zinc-300 hover:border-white/40 disabled:cursor-not-allowed disabled:opacity-40">Next</button>
        </nav>}
        {!loading && !loadError && resultCount > 0 && <p className="mt-3 text-center text-xs text-zinc-500">Menampilkan {(page - 1) * 12 + 1}–{Math.min(page * 12, resultCount)} dari {resultCount} engineer</p>}

        <section className="mt-12 rounded-2xl border border-white/10 bg-[#0a0a0c] p-6 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-400/10 flex items-center justify-center flex-shrink-0"><Check className="w-5 h-5 text-emerald-400" /></div>
            <div>
              <h3 className="font-semibold">Secure booking flow for registered customers</h3>
              <p className="text-xs text-zinc-500 mt-1">Sign in → choose a package → submit a brief → engineer accepts → pay via DOKU → project dashboard.</p>
            </div>
          </div>
          {preview && <span className="text-xs text-emerald-300 px-3 py-2 rounded-full border border-emerald-400/20 bg-emerald-400/5">Private workspace connected</span>}
        </section>
      </div>
    </div>
  );
}
